import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import leadsApi from "../api/leadsApi";
import campaignApi from "../api/campaignApi";
import { useAuthStore } from "../store/useAuthStore";
import { addDays, addMonths, today } from "./leadPipeline";

// --- helpers -----------------------------------------------------------

const emptyForm = {
  // company
  company: "",
  address: "",
  city: "",
  state: "",

  // team / primary contact
  designation: "",
  salutation: "Mr.",
  firstName: "",
  lastName: "",
  email: "",
  contactNo: "",
  setAsDefaultContact: true,
  sendIntroEmail: false,
  teamMembers: [], // additional contacts added via "+ Add team member"

  // classification
  source: "",
  priority: "Medium",
  campaign: "",
  isActive: false,

  // pipeline
  status: "New",
  statusTouched: false, // becomes true once user manually picks a status
  clientBudget: "",
  estValue: "",
  leadCreatedDate: "", // set to today when a new lead is opened
  lastContactDate: "",
  nextFollowUp: "",
  followUpTouched: false, // becomes true once user hand-edits follow up
  expectedClosing: "",
  expectedClosingTouched: false, // becomes true once user hand-edits closing date

  // legacy fields kept for backend compatibility
  contact: "",
  title: "",
  probability: "",
  response: "-",
  assignedTo: "",
};

const SOURCE_OPTIONS = [
  "Social Media",
  "Google",
  "Instagram",
  "Facebook",
  "LinkedIn",
  "Website",
  "Referral",
  "Conference",
  "Cold Email",
  "Phone Call",
  "Other",
];

const PRIORITY_OPTIONS = ["High", "Medium", "Low"];

const STATUS_OPTIONS = [
  "New",
  "Contacted",
  "Follow Up",
  "Interested",
  "Proposal",
  "Cold",
  "Client",
];

// --- period filter (Month / Quarter / Year) -----------------------------

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const QUARTERS = [
  { key: "q1", months: [0, 1, 2] }, // Jan-Mar
  { key: "q2", months: [3, 4, 5] }, // Apr-Jun
  { key: "q3", months: [6, 7, 8] }, // Jul-Sep
  { key: "q4", months: [9, 10, 11] }, // Oct-Dec
];

const getQuarterLabel = (quarter, year) => {
  const startMonth = MONTH_NAMES[quarter.months[0]];
  const endMonth = MONTH_NAMES[quarter.months[2]];
  const shortYear = String(year).slice(-2);
  return `${startMonth}-${endMonth}-${shortYear}`;
};

const getLeadDateForFilter = (lead) => lead?.leadCreatedDate || lead?.createdAt;

// Year dropdown list: current year + every year found in the leads data.
// This keeps the current year always visible (even with no data yet) and
// automatically grows as leads from new years get created.
const getAvailableYears = (leads) => {
  const years = new Set([new Date().getFullYear()]);

  leads.forEach((lead) => {
    const value = getLeadDateForFilter(lead);
    if (!value) return;
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      years.add(date.getFullYear());
    }
  });

  return [...years].sort((a, b) => a - b);
};

// Current week = Monday through Sunday, same convention used elsewhere in
// the CRM (meetings-this-week, etc).
const getCurrentWeekRange = () => {
  const now = new Date();

  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const day = start.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + diffToMonday);

  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

const isWithinFilter = (lead, { year, month, quarter, weekly }) => {
  const value = getLeadDateForFilter(lead);

  if (!value) return false;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;

  // This Week now works together with Year / Month / Quarter.
  // It no longer disables or resets the other date filters.
  if (weekly) {
    const { start, end } = getCurrentWeekRange();
    if (date < start || date > end) return false;
  }

  if (year !== "all" && date.getFullYear() !== Number(year)) return false;

  if (month !== "all") {
    if (date.getMonth() !== Number(month)) return false;
  } else if (quarter !== "all") {
    const q = QUARTERS.find((item) => item.key === quarter);
    if (q && !q.months.includes(date.getMonth())) return false;
  }

  return true;
};

const Leads = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authUser } = useAuthStore();

  const currentRole = String(authUser?.role || "")
    .trim()
    .toLowerCase();

  const isAdmin = currentRole === "admin";

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filterYear, setFilterYear] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterQuarter, setFilterQuarter] = useState("all");
  const [filterWeekly, setFilterWeekly] = useState(false);
  const [leadVisibility, setLeadVisibility] = useState("active");
  const [filterCampaign, setFilterCampaign] = useState("all");
  const [campaigns, setCampaigns] = useState([]);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showMoreTools, setShowMoreTools] = useState(false);
  const [leadSearch, setLeadSearch] = useState("");

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await leadsApi.getAll();
      setLeads(data);
    } catch (err) {
      // api.js's response interceptor already normalizes this to err.message
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const availableYears = useMemo(() => getAvailableYears(leads), [leads]);

  const fetchCampaigns = useCallback(async () => {
    setCampaignLoading(true);
    try {
      const data = await campaignApi.getAll();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Unable to load campaign master.");
    } finally {
      setCampaignLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const availableCampaigns = useMemo(() => {
    const masterNames = campaigns
      .filter((item) => item?.isActive !== false)
      .map((item) => item.name)
      .filter(Boolean);

    const oldLeadCampaigns = leads.map((lead) => lead.campaign).filter(Boolean);

    return [...new Set([...masterNames, ...oldLeadCampaigns])].sort((a, b) =>
      a.localeCompare(b),
    );
  }, [campaigns, leads]);

  // IMPORTANT:
  // Do NOT memoize the filtered list here.
  // Recalculate it on every render so every filter button/dropdown
  // updates the table immediately without browser refresh.
  const filteredLeads = leads.filter((lead) => {
    const isLeadActive =
      lead.isActive === true || String(lead.isActive).toLowerCase() === "true";

    if (leadVisibility === "active" && !isLeadActive) {
      return false;
    }

    if (leadVisibility === "inactive" && isLeadActive) {
      return false;
    }

    if (filterCampaign !== "all" && lead.campaign !== filterCampaign) {
      return false;
    }

    const searchTerm = leadSearch.trim().toLowerCase();

    if (searchTerm) {
      const leadName = `${lead.firstName || ""} ${lead.lastName || ""}`.trim();

      const searchableText = [
        leadName,
        lead.contact,
        lead.company,
        lead.email,
        lead.contactNo,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (!searchableText.includes(searchTerm)) {
        return false;
      }
    }

    return isWithinFilter(lead, {
      year: filterYear,
      month: filterMonth,
      quarter: filterQuarter,
      weekly: filterWeekly,
    });
  });

  // If we've navigated back from the Lead Detail page via its "Edit" button,
  // location.state.editLeadId tells us which lead to reopen in the modal.
  useEffect(() => {
    const editLeadId = location.state?.editLeadId;
    if (editLeadId && leads.length) {
      const lead = leads.find((l) => l._id === editLeadId);
      if (lead) {
        openEditModal(lead);
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, location.state]);

  const openAddModal = () => {
    const createdDate = today();
    setEditingId(null);
    setForm({
      ...emptyForm,
      leadCreatedDate: createdDate,
      expectedClosing: addMonths(createdDate, 1),
    });
    setShowMoreTools(false);
    setModalOpen(true);
  };

  const openEditModal = (lead) => {
    setEditingId(lead._id);
    setForm({
      ...emptyForm,
      ...lead,
      leadCreatedDate: lead.leadCreatedDate
        ? lead.leadCreatedDate.slice(0, 10)
        : lead.createdAt
          ? lead.createdAt.slice(0, 10)
          : "",
      lastContactDate: lead.lastContactDate
        ? lead.lastContactDate.slice(0, 10)
        : "",
      nextFollowUp: lead.nextFollowUp ? lead.nextFollowUp.slice(0, 10) : "",
      expectedClosing: lead.expectedClosing
        ? lead.expectedClosing.slice(0, 10)
        : "",
      followUpTouched: true, // don't auto-overwrite an existing lead's follow-up date
      expectedClosingTouched: true, // don't auto-overwrite an existing lead's closing date
    });
    setShowMoreTools(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Last Contacted On drives Follow-up On (+3 days), unless the user has
  // manually edited the follow-up date themselves for this lead.
  const handleLastContactChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      lastContactDate: value,
      nextFollowUp: prev.followUpTouched
        ? prev.nextFollowUp
        : addDays(value, 3),
    }));
  };

  const handleFollowUpChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      nextFollowUp: value,
      followUpTouched: true,
    }));
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, status: value, statusTouched: true }));
  };

  const resetFollowUpToAuto = () => {
    setForm((prev) => ({
      ...prev,
      nextFollowUp: addDays(prev.lastContactDate, 3),
      followUpTouched: false,
    }));
  };

  const handleExpectedClosingChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      expectedClosing: value,
      expectedClosingTouched: true,
    }));
  };

  const resetExpectedClosingToAuto = () => {
    setForm((prev) => ({
      ...prev,
      expectedClosing: addMonths(prev.leadCreatedDate || today(), 1),
      expectedClosingTouched: false,
    }));
  };

  const addTeamMember = () => {
    setForm((prev) => ({
      ...prev,
      teamMembers: [
        ...prev.teamMembers,
        {
          salutation: "Mr.",
          firstName: "",
          lastName: "",
          email: "",
          contactNo: "",
        },
      ],
    }));
  };

  const updateTeamMember = (index, field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const teamMembers = [...prev.teamMembers];
      teamMembers[index] = { ...teamMembers[index], [field]: value };
      return { ...prev, teamMembers };
    });
  };

  const removeTeamMember = (index) => {
    setForm((prev) => ({
      ...prev,
      teamMembers: prev.teamMembers.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        // keep legacy fields the API already expects populated
        contact: `${form.firstName} ${form.lastName}`.trim(),
        title: form.designation,
        clientBudget:
          form.clientBudget === "" ? null : Number(form.clientBudget),
        probability: form.probability === "" ? null : Number(form.probability),
      };

      // Estimated value is internal. Only Admin may submit this field.
      if (isAdmin) {
        payload.estValue = form.estValue === "" ? null : Number(form.estValue);
      } else {
        delete payload.estValue;
      }

      delete payload.followUpTouched;
      delete payload.expectedClosingTouched;

      if (editingId) {
        await leadsApi.update(editingId, payload);
      } else {
        // Every new lead starts as INACTIVE.
        // It will enter the normal Active list only after Activate is clicked.
        payload.isActive = false;
        await leadsApi.create(payload);
      }

      await fetchLeads();
      closeModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (lead) => {
    const currentActive =
      lead.isActive === true || String(lead.isActive).toLowerCase() === "true";

    const nextActive = !currentActive;

    // Optimistic UI update:
    // move the lead between Active/Inactive immediately,
    // without waiting for another GET request or page refresh.
    setLeads((prev) =>
      prev.map((item) =>
        item._id === lead._id ? { ...item, isActive: nextActive } : item,
      ),
    );

    setError(null);

    try {
      const updated = await leadsApi.update(lead._id, {
        isActive: nextActive,
      });

      // If API returns updated lead, merge it immediately.
      const updatedLead = updated?.data || updated;

      if (updatedLead && updatedLead._id) {
        setLeads((prev) =>
          prev.map((item) =>
            item._id === updatedLead._id ? { ...item, ...updatedLead } : item,
          ),
        );
      }
    } catch (err) {
      // Roll back UI if backend update fails.
      setLeads((prev) =>
        prev.map((item) =>
          item._id === lead._id ? { ...item, isActive: currentActive } : item,
        ),
      );

      setError(err.message || "Unable to update lead status.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await leadsApi.remove(id);
      await fetchLeads();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h2 style={s.title}>Leads</h2>

        <div style={s.headerActions}>
          <div style={s.periodFilter}>
            <button
              type="button"
              style={{
                ...s.weeklyButton,
                ...(leadVisibility === "active" ? s.weeklyButtonActive : null),
              }}
              onClick={() => setLeadVisibility("active")}
            >
              Active
            </button>

            <button
              type="button"
              style={{
                ...s.weeklyButton,
                ...(leadVisibility === "inactive"
                  ? s.weeklyButtonActive
                  : null),
              }}
              onClick={() => setLeadVisibility("inactive")}
            >
              Inactive
            </button>

            <button
              type="button"
              style={{
                ...s.weeklyButton,
                ...(leadVisibility === "all" ? s.weeklyButtonActive : null),
              }}
              onClick={() => setLeadVisibility("all")}
            >
              All
            </button>

            <button
              type="button"
              style={{
                ...s.weeklyButton,
                ...(filterWeekly ? s.weeklyButtonActive : null),
              }}
              onClick={() => {
                setFilterWeekly((prev) => !prev);
              }}
            >
              This Week
            </button>

            <select
              style={s.filterSelect}
              value={filterYear}
              onChange={(e) => {
                setFilterYear(e.target.value);
              }}
            >
              <option value="all">All Years</option>
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            <select
              style={s.filterSelect}
              value={filterMonth}
              onChange={(e) => {
                setFilterMonth(e.target.value);
                setFilterQuarter("all");
              }}
            >
              <option value="all">All Months</option>
              {MONTH_NAMES.map((name, index) => (
                <option key={name} value={index}>
                  {name}
                </option>
              ))}
            </select>

            <select
              style={s.filterSelect}
              value={filterQuarter}
              onChange={(e) => {
                setFilterQuarter(e.target.value);
                setFilterMonth("all");
              }}
            >
              <option value="all">All Quarters</option>
              {QUARTERS.map((q) => (
                <option key={q.key} value={q.key}>
                  {getQuarterLabel(
                    q,
                    filterYear === "all"
                      ? new Date().getFullYear()
                      : filterYear,
                  )}
                </option>
              ))}
            </select>

            <select
              style={s.filterSelect}
              value={filterCampaign}
              onChange={(e) => setFilterCampaign(e.target.value)}
            >
              <option value="all">All Campaigns</option>
              {availableCampaigns.map((campaign) => (
                <option key={campaign} value={campaign}>
                  {campaign}
                </option>
              ))}
            </select>
          </div>

          <div style={s.searchBox}>
            <span style={s.searchIcon}>⌕</span>

            <input
              type="text"
              value={leadSearch}
              onChange={(e) => setLeadSearch(e.target.value)}
              placeholder="Search lead name..."
              style={s.searchInput}
            />

            {leadSearch && (
              <button
                type="button"
                style={s.searchClearBtn}
                onClick={() => setLeadSearch("")}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          <div style={s.headerButtonGroup}>
            <button
              type="button"
              style={s.masterBtn}
              onClick={() => navigate("/campaign-master")}
            >
              Campaign Master
            </button>

            <button style={s.addBtn} onClick={openAddModal}>
              + Add Lead
            </button>
          </div>
        </div>
      </div>

      {error && <div style={s.errorBox}>{error}</div>}

      <div style={s.tableCard}>
        {loading ? (
          <div style={s.emptyState}>Loading leads…</div>
        ) : filteredLeads.length === 0 ? (
          <div style={s.emptyState}>
            {leads.length === 0 ? (
              <>
                No leads yet. Click <strong>Add Lead</strong> to create your
                first one.
              </>
            ) : leadSearch ? (
              `No lead found for "${leadSearch}".`
            ) : (
              "No leads found for this period."
            )}
          </div>
        ) : (
          <div style={s.tableScroll}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Company</th>
                  <th style={s.th}>Contact</th>
                  <th style={s.th}>Email</th>
                  <th style={s.th}>Source</th>
                  <th style={s.th}>Campaign</th>
                  <th style={s.th}>Priority</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Assigned To</th>
                  <th style={s.th}>Client Budget</th>
                  {isAdmin && <th style={s.th}>Est. Value</th>}
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => (
                  <tr
                    key={lead._id}
                    style={s.trClickable}
                    onClick={() => navigate(`/leads/${lead._id}`)}
                  >
                    <td style={s.td}>{lead.company}</td>
                    <td style={s.td}>{lead.contact}</td>
                    <td style={s.td}>{lead.email}</td>
                    <td style={s.td}>{lead.source || "–"}</td>
                    <td style={s.td}>{lead.campaign || "–"}</td>
                    <td style={s.td}>{lead.priority}</td>
                    <td style={s.td}>{lead.status}</td>
                    <td style={s.td}>{lead.assignedTo || "–"}</td>
                    <td style={s.td}>
                      {lead.clientBudget !== null &&
                      lead.clientBudget !== undefined &&
                      lead.clientBudget !== ""
                        ? `$${Number(lead.clientBudget).toLocaleString("en-US")}`
                        : "–"}
                    </td>
                    {isAdmin && (
                      <td style={s.td}>
                        {lead.estValue !== null &&
                        lead.estValue !== undefined &&
                        lead.estValue !== ""
                          ? `$${Number(lead.estValue).toLocaleString("en-US")}`
                          : "–"}
                      </td>
                    )}
                    <td style={s.td} onClick={(e) => e.stopPropagation()}>
                      <button
                        style={{
                          ...s.linkBtn,
                          color:
                            lead.isActive === true ||
                            String(lead.isActive).toLowerCase() === "true"
                              ? "#d97706"
                              : "#16a34a",
                        }}
                        onClick={() => handleToggleActive(lead)}
                      >
                        {lead.isActive === true ||
                        String(lead.isActive).toLowerCase() === "true"
                          ? "Make Inactive"
                          : "Activate"}
                      </button>

                      <button
                        style={s.linkBtn}
                        onClick={() => openEditModal(lead)}
                      >
                        Edit
                      </button>
                      <button
                        style={{ ...s.linkBtn, color: "#dc2626" }}
                        onClick={() => handleDelete(lead._id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div style={s.overlay} onClick={closeModal}>
          <form
            style={s.modal}
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit}
          >
            <h3 style={s.modalTitle}>{editingId ? "Edit Lead" : "Add Lead"}</h3>

            {/* Company */}
            <div style={s.card}>
              <SectionLabel>Company</SectionLabel>
              <Input
                placeholder="Company name"
                value={form.company}
                onChange={handleChange("company")}
                required
                full
              />
              <div style={s.row3}>
                <Input
                  placeholder="Address"
                  value={form.address}
                  onChange={handleChange("address")}
                />
                <Input
                  placeholder="City"
                  value={form.city}
                  onChange={handleChange("city")}
                />
                <Input
                  placeholder="State"
                  value={form.state}
                  onChange={handleChange("state")}
                />
              </div>
            </div>

            {/* Team / primary contact */}
            <div style={s.card}>
              <SectionLabel>Team</SectionLabel>
              <div style={s.row4}>
                <Select
                  value={form.salutation}
                  onChange={handleChange("salutation")}
                  options={["Mr.", "Ms.", "Mrs.", "Dr."]}
                />
                <Input
                  placeholder="First name"
                  value={form.firstName}
                  onChange={handleChange("firstName")}
                  required
                />
                <Input
                  placeholder="Last name"
                  value={form.lastName}
                  onChange={handleChange("lastName")}
                />
                <Input
                  placeholder="Designation"
                  value={form.designation}
                  onChange={handleChange("designation")}
                />
              </div>
              <div style={s.row2}>
                <Input
                  placeholder="Email"
                  type="email"
                  value={form.email}
                  onChange={handleChange("email")}
                  required
                />
                <Input
                  placeholder="Contact no."
                  value={form.contactNo}
                  onChange={handleChange("contactNo")}
                />
              </div>
              <label style={s.checkboxRow}>
                <input
                  type="checkbox"
                  checked={form.setAsDefaultContact}
                  onChange={handleChange("setAsDefaultContact")}
                />
                Set as default contact
              </label>

              <button
                type="button"
                style={s.addTeamMemberBtn}
                onClick={addTeamMember}
              >
                + Add team member
              </button>

              {form.teamMembers.map((member, i) => (
                <div key={i} style={s.row5}>
                  <Select
                    value={member.salutation}
                    onChange={updateTeamMember(i, "salutation")}
                    options={["Mr.", "Ms.", "Mrs.", "Dr."]}
                  />
                  <Input
                    placeholder="First name"
                    value={member.firstName}
                    onChange={updateTeamMember(i, "firstName")}
                  />
                  <Input
                    placeholder="Last name"
                    value={member.lastName}
                    onChange={updateTeamMember(i, "lastName")}
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    value={member.email}
                    onChange={updateTeamMember(i, "email")}
                  />
                  <div style={s.teamMemberActions}>
                    <Input
                      placeholder="Contact no."
                      value={member.contactNo}
                      onChange={updateTeamMember(i, "contactNo")}
                    />
                    <button
                      type="button"
                      style={s.removeBtn}
                      onClick={() => removeTeamMember(i)}
                      aria-label="Remove team member"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}

              <label style={s.checkboxRow}>
                <input
                  type="checkbox"
                  checked={form.sendIntroEmail}
                  onChange={handleChange("sendIntroEmail")}
                />
                Send introductory email
              </label>
            </div>

            {/* Source / Campaign / Priority */}
            <div style={s.card}>
              <SectionLabel>Classification</SectionLabel>
              <div style={s.row3Equal}>
                <Select
                  value={form.source}
                  onChange={handleChange("source")}
                  options={SOURCE_OPTIONS}
                  placeholder="Select source"
                />

                <Select
                  value={form.campaign}
                  onChange={handleChange("campaign")}
                  options={availableCampaigns}
                  placeholder={
                    campaignLoading ? "Loading campaigns..." : "Select campaign"
                  }
                />

                <div style={s.priorityWrap}>
                  <Select
                    value={form.priority}
                    onChange={handleChange("priority")}
                    options={PRIORITY_OPTIONS}
                  />
                  <button
                    type="button"
                    style={s.moreToolsBtn}
                    onClick={() => setShowMoreTools((v) => !v)}
                  >
                    {showMoreTools ? "Fewer tools ▲" : "More tools ▼"}
                  </button>
                </div>
              </div>

              {showMoreTools && (
                <div style={s.row2}>
                  <Input
                    placeholder="Probability (%)"
                    type="number"
                    value={form.probability}
                    onChange={handleChange("probability")}
                  />
                  <Input
                    placeholder="Assigned to"
                    value={form.assignedTo}
                    onChange={handleChange("assignedTo")}
                  />
                </div>
              )}
            </div>

            {/* Auto-tracked pipeline fields */}
            <div style={s.autoPanel}>
              <div style={s.autoPanelHeader}>
                <span style={s.autoDot} />
                <span style={s.autoPanelTitle}>Auto-tracked</span>
                <span style={s.autoPanelSub}>
                  fills in automatically once email sync is connected — editable
                  anytime
                </span>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isAdmin ? "1fr 1fr 1fr" : "1fr 1fr",
                  gap: 14,
                  alignItems: "start",
                  marginBottom: 12,
                }}
              >
                {/* Status */}

                <div>
                  <Select
                    value={form.status}
                    onChange={handleStatusChange}
                    options={STATUS_OPTIONS}
                    placeholder="Status"
                    light={!form.statusTouched}
                  />

                  <div style={s.hint}>Auto-updates from inbox activity.</div>
                </div>

                {/* Client Budget */}

                <div>
                  <Input
                    placeholder="Client Budget (USD)"
                    type="number"
                    min="0"
                    value={form.clientBudget}
                    onChange={handleChange("clientBudget")}
                    light={!form.clientBudget}
                  />

                  <div style={s.hint}>Budget shared by the client.</div>
                </div>

                {/* Estimated Value */}

                {isAdmin && (
                  <div>
                    <Input
                      placeholder="Estimated Value (USD)"
                      type="number"
                      min="0"
                      value={form.estValue}
                      onChange={handleChange("estValue")}
                      light={!form.estValue}
                    />

                    <div style={s.hint}>
                      Internal value. Only Admin can view or update it.
                    </div>
                  </div>
                )}
              </div>

              <div style={s.row2}>
                <div>
                  <DateLabel>Last contacted on</DateLabel>
                  <Input
                    type="date"
                    value={form.lastContactDate}
                    onChange={handleLastContactChange}
                    light={!form.lastContactDate}
                  />
                  <div style={s.hint}>
                    Pulled from mail/other sources once connected.
                  </div>
                </div>
                <div>
                  <DateLabel>Follow up on</DateLabel>
                  <Input
                    type="date"
                    value={form.nextFollowUp}
                    onChange={handleFollowUpChange}
                    light={!form.nextFollowUp}
                  />
                  <div style={s.hint}>
                    {form.followUpTouched ? (
                      <>
                        Set manually.{" "}
                        <button
                          type="button"
                          style={s.linkBtnSmall}
                          onClick={resetFollowUpToAuto}
                        >
                          Reset to +3 days
                        </button>
                      </>
                    ) : (
                      "Auto-set to 3 days after last contact."
                    )}
                  </div>
                </div>
              </div>

              <div style={s.row2}>
                <div>
                  <DateLabel>Expected closing date</DateLabel>
                  <Input
                    type="date"
                    value={form.expectedClosing}
                    onChange={handleExpectedClosingChange}
                  />
                  <div style={s.hint}>
                    {form.expectedClosingTouched ? (
                      <>
                        Set manually.{" "}
                        <button
                          type="button"
                          style={s.linkBtnSmall}
                          onClick={resetExpectedClosingToAuto}
                        >
                          Reset to +1 month
                        </button>
                      </>
                    ) : (
                      "Auto-set to 1 month after lead creation."
                    )}
                  </div>
                </div>
                <div />
              </div>
            </div>

            <div style={s.modalActions}>
              <button type="button" style={s.cancelBtn} onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" style={s.saveBtn} disabled={saving}>
                {saving ? "Saving…" : editingId ? "Save Changes" : "Add Lead"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// --- small presentational helpers --------------------------------------

const SectionLabel = ({ children }) => (
  <div style={s.sectionLabel}>{children}</div>
);

// type="date" inputs ignore the placeholder attribute and always show the
// browser's own dd-mm-yyyy format, so these fields get a small visible
// label instead (unlike the other placeholder-only fields).
const DateLabel = ({ children }) => <div style={s.dateLabel}>{children}</div>;

const Input = ({ full, light, ...props }) => (
  <input
    {...props}
    style={{
      ...s.input,
      ...(full ? s.inputFull : null),
      ...(light ? s.inputLight : null),
    }}
  />
);

const Select = ({ value, onChange, options, placeholder, light }) => (
  <select
    value={value}
    onChange={onChange}
    style={{ ...s.input, ...(light ? s.inputLight : null) }}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((opt) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>
);

const s = {
  page: {
    padding: 20,
    fontFamily: "-apple-system, Segoe UI, Roboto, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  title: { margin: 0, fontSize: 20, fontWeight: 800, color: "#111a3d" },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    width: 250,
    minHeight: 38,
    padding: "0 10px",
    border: "1px solid #dce4f0",
    borderRadius: 8,
    background: "#fff",
    boxSizing: "border-box",
  },
  searchIcon: {
    fontSize: 18,
    color: "#64748b",
    marginRight: 7,
    lineHeight: 1,
  },
  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#334155",
    fontSize: 13,
    fontFamily: "inherit",
    minWidth: 0,
  },
  searchClearBtn: {
    border: "none",
    background: "transparent",
    color: "#94a3b8",
    fontSize: 18,
    cursor: "pointer",
    lineHeight: 1,
    padding: "0 2px",
  },
  headerButtonGroup: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  periodFilter: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: 5,
    border: "1px solid #dce4f0",
    borderRadius: 10,
    background: "#fff",
    boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
  },
  periodFilterLabel: {
    padding: "0 7px",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
  },
  filterSelect: {
    minHeight: 32,
    padding: "0 8px",
    border: "1px solid #dce4f0",
    borderRadius: 7,
    background: "#fff",
    color: "#334155",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  weeklyButton: {
    minHeight: 32,
    padding: "0 12px",
    border: "1px solid #dce4f0",
    borderRadius: 7,
    background: "#fff",
    color: "#475569",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  weeklyButtonActive: {
    background: "#111a3d",
    borderColor: "#111a3d",
    color: "#fff",
  },
  masterBtn: {
    background: "#fff",
    color: "#111a3d",
    border: "1px solid #111a3d",
    padding: "10px 14px",
    borderRadius: 8,
    fontWeight: 700,
    cursor: "pointer",
  },
  addBtn: {
    background: "#111a3d",
    color: "#fff",
    border: "none",
    padding: "10px 16px",
    borderRadius: 8,
    fontWeight: 700,
    cursor: "pointer",
  },
  errorBox: {
    background: "#fee2e2",
    color: "#b91c1c",
    padding: "10px 14px",
    borderRadius: 8,
    marginBottom: 14,
    fontSize: 13,
  },
  tableCard: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #e7eaf2",
    boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
    overflow: "hidden",
  },
  emptyState: {
    padding: 40,
    textAlign: "center",
    color: "#64748b",
    fontSize: 14,
  },
  tableScroll: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left",
    padding: "12px 14px",
    background: "#f8fafc",
    color: "#64748b",
    fontWeight: 700,
    fontSize: 11.5,
    borderBottom: "1px solid #e7eaf2",
  },
  td: {
    padding: "12px 14px",
    borderBottom: "1px solid #eef1f7",
    color: "#334155",
  },
  trClickable: {
    cursor: "pointer",
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: "#2563eb",
    fontWeight: 700,
    cursor: "pointer",
    marginRight: 12,
    fontSize: 12.5,
    padding: 0,
  },
  linkBtnSmall: {
    background: "none",
    border: "none",
    color: "#2563eb",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 11.5,
    padding: 0,
    textDecoration: "underline",
  },
  overlay: {
    position: "fixed",
    left: 0,
    right: 0,
    top: 64,
    bottom: 0,
    background: "rgba(15,23,42,0.45)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    zIndex: 9999,
    padding: "18px 20px 20px",
    overflowY: "auto",
    boxSizing: "border-box",
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    padding: 24,
    width: "100%",
    maxWidth: 720,
    maxHeight: "calc(100vh - 102px)",
    overflowY: "auto",
    boxSizing: "border-box",
    margin: 0,
  },
  modalTitle: {
    margin: "0 0 16px",
    fontSize: 17,
    fontWeight: 800,
    color: "#111a3d",
  },
  card: {
    border: "1px solid #eef1f7",
    borderRadius: 12,
    padding: 16,
    marginBottom: 14,
  },
  autoPanel: {
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
    borderRadius: 12,
    padding: 16,
    marginBottom: 6,
  },
  autoPanelHeader: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  autoDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#2563eb",
    display: "inline-block",
  },
  autoPanelTitle: {
    fontSize: 11,
    fontWeight: 800,
    color: "#334155",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  autoPanelSub: {
    fontSize: 11,
    color: "#94a3b8",
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 800,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    margin: "16px 0 8px",
  },
  divider: {
    borderTop: "1px solid #e7eaf2",
    margin: "16px 0",
  },
  row2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 12,
  },
  row3: {
    display: "grid",
    gridTemplateColumns: "1.4fr 1fr 1fr",
    gap: 12,
    marginBottom: 8,
  },
  row3Equal: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 12,
    marginBottom: 8,
  },
  row4: {
    display: "grid",
    gridTemplateColumns: "0.8fr 1fr 1fr 1fr",
    gap: 8,
    marginBottom: 8,
  },
  row5: {
    display: "grid",
    gridTemplateColumns: "0.8fr 1fr 1fr 1.2fr 1fr",
    gap: 8,
    marginBottom: 8,
  },
  teamMemberActions: {
    display: "flex",
    gap: 6,
    alignItems: "center",
  },
  removeBtn: {
    background: "#fee2e2",
    color: "#b91c1c",
    border: "none",
    borderRadius: 6,
    width: 28,
    height: 34,
    fontWeight: 800,
    cursor: "pointer",
    flexShrink: 0,
  },
  addTeamMemberBtn: {
    background: "none",
    border: "none",
    color: "#2563eb",
    fontWeight: 700,
    fontSize: 12.5,
    cursor: "pointer",
    padding: "4px 0 12px",
    textAlign: "left",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12.5,
    fontWeight: 600,
    color: "#475569",
    margin: "4px 0 12px",
  },
  priorityWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  moreToolsBtn: {
    background: "none",
    border: "none",
    color: "#64748b",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    padding: 0,
    textAlign: "right",
  },
  hint: {
    fontSize: 11,
    color: "#94a3b8",
    marginTop: 4,
    lineHeight: 1.4,
  },
  dateLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "#475569",
    marginBottom: 4,
  },
  input: {
    padding: "9px 10px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    fontSize: 13,
    fontFamily: "inherit",
    width: "100%",
    boxSizing: "border-box",
    background: "#fff",
    color: "#1e293b",
  },
  inputLight: {
    background: "#f1f5f9",
    borderColor: "#e2e8f0",
    color: "#94a3b8",
  },
  inputFull: {
    marginBottom: 8,
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  cancelBtn: {
    background: "#f1f5f9",
    color: "#334155",
    border: "none",
    padding: "10px 16px",
    borderRadius: 8,
    fontWeight: 700,
    cursor: "pointer",
  },
  saveBtn: {
    background: "#111a3d",
    color: "#fff",
    border: "none",
    padding: "10px 16px",
    borderRadius: 8,
    fontWeight: 700,
    cursor: "pointer",
  },
};

export default Leads;
